import { useState, useRef } from 'react';
import { parsePptxApi } from '../../../../api/apiServices';

function formatFileSize(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// Splits an array of slides into `count` contiguous groups as evenly as
// possible, keeping original slide order (any remainder slides go to the
// earliest groups) -- e.g. 10 slides into 3 groups gives [4, 3, 3].
function splitIntoGroups(items, count) {
  const total = items.length;
  const n = Math.max(1, Math.min(count, total || 1));
  const base = Math.floor(total / n);
  const remainder = total % n;
  const groups = [];
  let idx = 0;
  for (let i = 0; i < n; i++) {
    const size = base + (i < remainder ? 1 : 0);
    groups.push(items.slice(idx, idx + size));
    idx += size;
  }
  return groups;
}

// A deck gets a chance to be split into multiple lessons once it has more
// than this many slides -- a handful of slides is comfortably one lesson,
// so there's no reason to interrupt the import with an extra question.
const SPLIT_PROMPT_THRESHOLD = 3;

export default function PptxImportModal({ onClose, onSave }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'split' | 'preview'
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState('');
  const [error, setError] = useState('');

  const [deckTitle, setDeckTitle] = useState('');
  const [rawSlides, setRawSlides] = useState([]); // the full, unsplit deck
  const [lessonCount, setLessonCount] = useState(1);
  const [lessonGroups, setLessonGroups] = useState([]); // [{ id, title, slides }]

  const fileInputRef = useRef(null);
  const canSplit = rawSlides.length > SPLIT_PROMPT_THRESHOLD;

  const pickFile = (selected) => {
    if (!selected) return;
    if (!/\.pptx$/i.test(selected.name)) {
      setError('Please choose a .pptx file (older .ppt files are not supported -- re-save as .pptx first)');
      return;
    }
    setError('');
    setFile(selected);
  };

  const buildLessonGroups = (slides, title, count) => {
    const chunks = splitIntoGroups(slides, count);
    return chunks.map((chunk, i) => ({
      id: `lesson-group-${Date.now()}-${i}`,
      title: chunks.length > 1 ? `${title} - Part ${i + 1}` : title,
      slides: chunk,
    }));
  };

  const handleImport = async () => {
    if (!file) return;
    setParsing(true);
    setError('');

    try {
      const result = await parsePptxApi(file);
      if (result.success) {
        const title = result.deckTitle || file.name.replace(/\.pptx$/i, '');
        const imported = (result.slides || []).map((s, i) => ({
          id: `pptx-slide-${i}-${Date.now()}`,
          title: s.title || '',
          content: s.content || '',
          imageUrl: s.imageUrl || '',
          order: i,
        }));

        setDeckTitle(title);
        setRawSlides(imported);

        if (imported.length > SPLIT_PROMPT_THRESHOLD) {
          setLessonCount(1);
          setStep('split');
        } else {
          setLessonGroups(buildLessonGroups(imported, title, 1));
          setStep('preview');
        }
      } else {
        setError(result.message || 'Could not import slides from that file');
      }
    } catch (err) {
      console.error('PPTX import failed:', err);
      setError(err.response?.data?.message || 'Could not import slides from that file. Please try again.');
    } finally {
      setParsing(false);
    }
  };

  const confirmSplit = () => {
    setLessonGroups(buildLessonGroups(rawSlides, deckTitle, lessonCount));
    setStep('preview');
  };

  const updateGroupTitle = (groupIndex, value) => {
    setLessonGroups((prev) => prev.map((g, i) => (i === groupIndex ? { ...g, title: value } : g)));
  };

  const updateSlide = (groupIndex, slideIndex, field, value) => {
    setLessonGroups((prev) =>
      prev.map((g, i) =>
        i !== groupIndex
          ? g
          : { ...g, slides: g.slides.map((s, j) => (j === slideIndex ? { ...s, [field]: value } : s)) }
      )
    );
  };

  const removeSlide = (groupIndex, slideIndex) => {
    setLessonGroups((prev) =>
      prev.map((g, i) => (i !== groupIndex ? g : { ...g, slides: g.slides.filter((_, j) => j !== slideIndex) }))
    );
  };

  const addBlankSlide = (groupIndex) => {
    setLessonGroups((prev) =>
      prev.map((g, i) =>
        i !== groupIndex
          ? g
          : { ...g, slides: [...g.slides, { id: `slide-${Date.now()}`, title: '', content: '', imageUrl: '', order: g.slides.length }] }
      )
    );
  };

  const moveSlide = (groupIndex, slideIndex, direction) => {
    setLessonGroups((prev) =>
      prev.map((g, i) => {
        if (i !== groupIndex) return g;
        const target = slideIndex + direction;
        if (target < 0 || target >= g.slides.length) return g;
        const nextSlides = [...g.slides];
        [nextSlides[slideIndex], nextSlides[target]] = [nextSlides[target], nextSlides[slideIndex]];
        return { ...g, slides: nextSlides };
      })
    );
  };

  const handleSave = async () => {
    if (lessonGroups.some((g) => !g.title.trim())) {
      setError('Give every lesson a title before saving');
      return;
    }
    if (lessonGroups.some((g) => g.slides.length === 0)) {
      setError('Every lesson needs at least one slide before saving');
      return;
    }

    setSaving(true);
    setError('');

    const lessonsPayload = lessonGroups.map((g, i) => ({
      id: `lesson-${Date.now()}-${i}`,
      title: g.title.trim(),
      type: 'slides',
      slides: g.slides.map((s, j) => ({ ...s, order: j })),
      duration: '',
      isPreview: false,
      quiz: { title: 'Lesson Quiz', questions: [] },
    }));

    try {
      await onSave(lessonsPayload, (progress) => {
        if (progress?.total > 1) {
          setSavingProgress(`Saving lesson ${progress.current} of ${progress.total}: "${progress.title}"`);
        }
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save these lessons. Please try again.');
      setSaving(false);
      setSavingProgress('');
    }
  };

  const totalSlideCount = lessonGroups.reduce((n, g) => n + g.slides.length, 0);
  const splitPreview = canSplit ? splitIntoGroups(rawSlides, lessonCount) : [];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Import from PowerPoint</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Upload a .pptx deck and turn it into one or more slide lessons in seconds
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-2xl cursor-pointer">
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="px-4 py-3 rounded-2xl border bg-rose-50 border-rose-200 text-rose-700 flex items-center gap-3 text-sm font-medium">
              <i className="ri-error-warning-line text-lg flex-shrink-0" />
              {error}
            </div>
          )}

          {step === 'upload' && (
            <>
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  pickFile(e.dataTransfer.files?.[0]);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${
                  isDragging ? 'border-teal-500 bg-teal-50' : 'border-slate-300 hover:border-teal-400 hover:bg-slate-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  className="hidden"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />
                <div className="w-14 h-14 mx-auto mb-4 bg-teal-50 rounded-2xl flex items-center justify-center">
                  <i className="ri-file-ppt-2-line text-3xl text-teal-600" />
                </div>
                {file ? (
                  <>
                    <p className="font-semibold text-slate-800">{file.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{formatFileSize(file.size)} &middot; click to choose a different file</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-slate-700">Drag & drop your .pptx file here</p>
                    <p className="text-xs text-slate-500 mt-1">or click to browse your computer</p>
                  </>
                )}
              </div>

              <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 flex items-start gap-2.5">
                <i className="ri-information-line text-teal-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-teal-700 leading-relaxed">
                  Each slide is imported as a full image, exactly as it looks in PowerPoint -- no text is extracted or
                  retyped. If your deck has more than {SPLIT_PROMPT_THRESHOLD} slides, you'll get to choose whether to
                  split it into several lessons before it's saved. Rendering every slide can take a little longer than
                  a quick text scan, especially for larger decks -- hang tight while it works.
                </p>
              </div>
            </>
          )}

          {step === 'split' && (
            <>
              <div className="text-center py-2">
                <div className="w-14 h-14 mx-auto mb-4 bg-teal-50 rounded-2xl flex items-center justify-center">
                  <i className="ri-scissors-cut-line text-2xl text-teal-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  This deck has {rawSlides.length} slides
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  How many lessons would you like to create from it? Choose 1 to keep it as a single lesson.
                </p>
              </div>

              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setLessonCount((n) => Math.max(1, n - 1))}
                  disabled={lessonCount <= 1}
                  className="w-11 h-11 flex items-center justify-center rounded-2xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 cursor-pointer"
                >
                  <i className="ri-subtract-line text-lg" />
                </button>
                <input
                  type="number"
                  min={1}
                  max={rawSlides.length}
                  value={lessonCount}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setLessonCount(Number.isNaN(val) ? 1 : Math.max(1, Math.min(rawSlides.length, val)));
                  }}
                  className="w-20 text-center px-3 py-2.5 border border-slate-200 rounded-2xl text-lg font-bold focus:outline-none focus:border-teal-500"
                />
                <button
                  onClick={() => setLessonCount((n) => Math.min(rawSlides.length, n + 1))}
                  disabled={lessonCount >= rawSlides.length}
                  className="w-11 h-11 flex items-center justify-center rounded-2xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 cursor-pointer"
                >
                  <i className="ri-add-line text-lg" />
                </button>
                <span className="text-sm text-slate-500 ml-1">lesson{lessonCount === 1 ? '' : 's'}</span>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2">
                {[1, 2, 3, 4, 5].filter((n) => n <= rawSlides.length).map((n) => (
                  <button
                    key={n}
                    onClick={() => setLessonCount(n)}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold border cursor-pointer transition-colors ${
                      lessonCount === n
                        ? 'bg-teal-600 border-teal-600 text-white'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-teal-300'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Preview</p>
                <div className="space-y-1.5">
                  {splitPreview.map((chunk, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700 font-medium">
                        {splitPreview.length > 1 ? `${deckTitle} - Part ${i + 1}` : deckTitle}
                      </span>
                      <span className="text-slate-400">{chunk.length} slide{chunk.length === 1 ? '' : 's'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 'preview' && (
            <>
              {lessonGroups.map((group, groupIndex) => (
                <div key={group.id} className={lessonGroups.length > 1 ? 'border border-slate-200 rounded-2xl p-5 space-y-4' : 'space-y-4'}>
                  {lessonGroups.length > 1 && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 border border-teal-100 rounded-full text-teal-700 text-xs font-bold">
                      <i className="ri-book-2-line" /> Lesson {groupIndex + 1} of {lessonGroups.length}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Lesson Title *</label>
                    <input
                      type="text"
                      value={group.title}
                      onChange={(e) => updateGroupTitle(groupIndex, e.target.value)}
                      placeholder="e.g. Understanding React Hooks"
                      className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-700">Slides ({group.slides.length})</label>
                    <button
                      onClick={() => addBlankSlide(groupIndex)}
                      className="flex items-center gap-1.5 px-3.5 py-2 bg-teal-600 text-white text-xs font-semibold rounded-xl hover:bg-teal-700 cursor-pointer"
                    >
                      <i className="ri-add-line" /> Add Slide
                    </button>
                  </div>

                  <div className="space-y-4">
                    {group.slides.map((slide, index) => (
                      <div key={slide.id} className="border border-slate-200 rounded-2xl p-5 bg-slate-50">
                        <div className="flex items-center justify-between mb-3">
                          <span className="flex items-center gap-2 font-semibold text-slate-600 text-sm">
                            <span className="w-6 h-6 flex items-center justify-center bg-white border border-slate-200 rounded-lg text-xs font-mono">
                              {index + 1}
                            </span>
                            Slide {index + 1}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => moveSlide(groupIndex, index, -1)}
                              disabled={index === 0}
                              title="Move up"
                              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
                            >
                              <i className="ri-arrow-up-line" />
                            </button>
                            <button
                              onClick={() => moveSlide(groupIndex, index, 1)}
                              disabled={index === group.slides.length - 1}
                              title="Move down"
                              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
                            >
                              <i className="ri-arrow-down-line" />
                            </button>
                            <button
                              onClick={() => removeSlide(groupIndex, index)}
                              title="Delete slide"
                              className="w-7 h-7 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg cursor-pointer"
                            >
                              <i className="ri-delete-bin-line" />
                            </button>
                          </div>
                        </div>

                        {slide.imageUrl ? (
                          // Imported slides are a full image of the slide itself --
                          // shown large, with nothing else to edit (there's no
                          // extracted text sitting alongside it to keep in sync).
                          <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
                            <img src={slide.imageUrl} alt={`Slide ${index + 1}`} className="w-full h-auto max-h-72 object-contain bg-slate-100" />
                          </div>
                        ) : (
                          // A manually-added blank slide (via "Add Slide" above) has
                          // no image, so it falls back to a plain text slide instead.
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={slide.title}
                              onChange={(e) => updateSlide(groupIndex, index, 'title', e.target.value)}
                              placeholder="Slide title"
                              className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-teal-500 bg-white"
                            />
                            <textarea
                              value={slide.content}
                              onChange={(e) => updateSlide(groupIndex, index, 'content', e.target.value)}
                              rows={3}
                              placeholder="One bullet point per line..."
                              className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 resize-y bg-white"
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    {group.slides.length === 0 && (
                      <div className="text-center py-12 border border-dashed border-slate-300 rounded-2xl">
                        <p className="text-slate-400">No slides left. Click "Add Slide" to add one.</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="border-t border-slate-100 p-6 flex justify-between items-center flex-shrink-0">
          <button
            onClick={() => {
              if (step === 'preview') {
                setStep(canSplit ? 'split' : 'upload');
              } else if (step === 'split') {
                setStep('upload');
              } else {
                onClose();
              }
            }}
            className="px-6 py-3 border border-slate-200 rounded-2xl font-medium text-slate-600 hover:bg-slate-50 cursor-pointer"
          >
            {step === 'upload' ? 'Cancel' : 'Back'}
          </button>

          {step === 'upload' && (
            <button
              onClick={handleImport}
              disabled={!file || parsing}
              className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-2xl hover:bg-teal-700 disabled:opacity-50 transition-colors cursor-pointer flex items-center gap-2"
            >
              {parsing ? (
                <><i className="ri-loader-4-line animate-spin" /> Importing Slides...</>
              ) : (
                <><i className="ri-magic-line" /> Import Slides</>
              )}
            </button>
          )}

          {step === 'split' && (
            <button
              onClick={confirmSplit}
              className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-2xl hover:bg-teal-700 transition-colors cursor-pointer flex items-center gap-2"
            >
              <i className="ri-arrow-right-line" /> Continue
            </button>
          )}

          {step === 'preview' && (
            <button
              onClick={handleSave}
              disabled={saving || lessonGroups.length === 0 || lessonGroups.some((g) => !g.title.trim() || g.slides.length === 0)}
              className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-2xl hover:bg-teal-700 disabled:opacity-50 transition-colors cursor-pointer flex items-center gap-2"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin" /> {savingProgress || 'Saving...'}</>
              ) : (
                <><i className="ri-check-line" /> {lessonGroups.length > 1 ? `Add ${lessonGroups.length} Lessons (${totalSlideCount} slides)` : 'Add as Lesson'}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
